import { useState, useRef } from 'react'

const BrowserApp = () => {
  // Default tabs featuring Pink Pixel projects
  const defaultTabs = [
    {
      id: 'portfolio',
      title: 'Pink Pixel Portfolio',
      url: 'https://portfolio.pinkpixel.dev',
      favicon: '💗',
      allowFrame: true
    },
	{
	  id: 'cloudwerx',
	  title: 'CLOUDWERX LAB',
      url: 'https://cloudwerxlab.com/',
      favicon: '☁️',
      allowFrame: true
    },
	{
      id: 'links',
      title: 'Pink Pixel Links',
      url: 'about:pinkpixel',
      favicon: '✨',
      allowFrame: true,
      isCustomPage: true
    },

  ]

  const [tabs, setTabs] = useState(defaultTabs)
  const [activeTabId, setActiveTabId] = useState('portfolio')
  const [addressBarUrl, setAddressBarUrl] = useState('https://portfolio.pinkpixel.dev')
  const [isLoading, setIsLoading] = useState(false)
  const [frameError, setFrameError] = useState(false)
  const [history, setHistory] = useState({
    portfolio: ['https://portfolio.pinkpixel.dev'],
    cloudwerx: ['https://cloudwerxlab.com/'],
    links: ['about:pinkpixel']
  })
  const [currentIndex, setCurrentIndex] = useState({
    portfolio: 0,
    cloudwerx: 0,
    links: 0
  })
  const iframeRef = useRef(null)

  const activeTab = tabs.find(tab => tab.id === activeTabId)

  const handleNavigate = (url, addToHistory = true) => {
    setIsLoading(true)
    setFrameError(false)
    setAddressBarUrl(url)
    
    // Add to history if this is a new navigation (not back/forward)
    if (addToHistory) {
      const tabHistory = history[activeTabId] || []
      const tabIndex = currentIndex[activeTabId] || 0
      
      // Remove any forward history and add new URL
      const newHistory = [...tabHistory.slice(0, tabIndex + 1), url]
      
      setHistory(prev => ({ ...prev, [activeTabId]: newHistory }))
      setCurrentIndex(prev => ({ ...prev, [activeTabId]: newHistory.length - 1 }))
    }
    
    // Update the active tab's URL and remove custom page flag
    setTabs(prevTabs => 
      prevTabs.map(tab => 
        tab.id === activeTabId 
          ? { ...tab, url, title: getPageTitle(url), isCustomPage: url === 'about:pinkpixel' }
          : tab
      )
    )
    
    // Clear loading after a short delay in case iframe doesn't load
    setTimeout(() => {
      setIsLoading(false)
    }, 3000)
  }

  const handleAddressBarSubmit = (e) => {
    e.preventDefault()
    let url = addressBarUrl.trim()
    
    // Add protocol if missing
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }
    
    handleNavigate(url)
  }

  const handleGoBack = () => {
    const tabHistory = history[activeTabId] || []
    const tabIndex = currentIndex[activeTabId] || 0
    
    if (tabIndex > 0) {
      const newIndex = tabIndex - 1
      const previousUrl = tabHistory[newIndex]
      
      setCurrentIndex(prev => ({ ...prev, [activeTabId]: newIndex }))
      handleNavigate(previousUrl, false) // Don't add to history
    }
  }

  const handleGoForward = () => {
    const tabHistory = history[activeTabId] || []
    const tabIndex = currentIndex[activeTabId] || 0
    
    if (tabIndex < tabHistory.length - 1) {
      const newIndex = tabIndex + 1
      const nextUrl = tabHistory[newIndex]
      
      setCurrentIndex(prev => ({ ...prev, [activeTabId]: newIndex }))
      handleNavigate(nextUrl, false) // Don't add to history
    }
  }

  const handleRefresh = () => {
    if (iframeRef.current) {
      setIsLoading(true)
      iframeRef.current.src = iframeRef.current.src
    }
  }

  const handleIframeLoad = () => {
    setIsLoading(false)
    setFrameError(false)
  }

  const handleIframeError = () => {
    setIsLoading(false)
    setFrameError(true)
  }

  const getPageTitle = (url) => {
    if (url.includes('portfolio.pinkpixel.dev')) return 'Pink Pixel Portfolio'
    if (url.includes('github.com')) return 'Pink Pixel GitHub'
    if (url.includes('buymeacoffee.com')) return 'Buy Me a Coffee'
    if (url.includes('google.com')) return 'Google Search'
    
    // Extract domain name for title
    try {
      const domain = new URL(url).hostname.replace('www.', '')
      return domain.charAt(0).toUpperCase() + domain.slice(1)
    } catch {
      return 'New Tab'
    }
  }

  const addNewTab = () => {
    const newTab = {
      id: `tab-${Date.now()}`,
      title: 'New Tab',
      url: 'https://www.google.com',
      favicon: '🌐'
    }
    setTabs(prev => [...prev, newTab])
    setActiveTabId(newTab.id)
    setAddressBarUrl(newTab.url)
    
    // Initialize history for new tab
    setHistory(prev => ({ ...prev, [newTab.id]: [newTab.url] }))
    setCurrentIndex(prev => ({ ...prev, [newTab.id]: 0 }))
  }

  const closeTab = (tabId, e) => {
    e.stopPropagation()
    if (tabs.length === 1) return // Don't close the last tab
    
    const tabIndex = tabs.findIndex(tab => tab.id === tabId)
    const newTabs = tabs.filter(tab => tab.id !== tabId)
    setTabs(newTabs)
    
    if (activeTabId === tabId) {
      const newActiveTab = newTabs[Math.min(tabIndex, newTabs.length - 1)]
      setActiveTabId(newActiveTab.id)
      setAddressBarUrl(newActiveTab.url)
    }
  }

  const switchTab = (tabId) => {
    setActiveTabId(tabId)
    const tab = tabs.find(t => t.id === tabId)
    if (tab) {
      setAddressBarUrl(tab.url)
    }
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-blue-900/20 to-purple-900/20 text-white">
      {/* Tab Bar */}
      <div className="flex items-end bg-gray-800/30 px-2 pt-2 border-b border-white/10">
        <div className="flex space-x-1 flex-1 overflow-x-auto">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={`group flex items-center space-x-2 px-4 py-2 rounded-t-lg cursor-pointer transition-all min-w-[200px] max-w-[200px] ${
                activeTabId === tab.id 
                  ? 'bg-white/10 text-white border-b-2 border-blue-400' 
                  : 'bg-white/5 text-gray-300 hover:bg-white/8'
              }`}
            >
              <span className="text-sm">{tab.favicon}</span>
              <span className="text-sm truncate flex-1">{tab.title}</span>
              {tabs.length > 1 && (
                <button
                  onClick={(e) => closeTab(tab.id, e)}
                  className="opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded p-1 transition-all"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={addNewTab}
          className="p-2 hover:bg-white/10 rounded-t-lg transition-colors text-gray-300 hover:text-white"
          title="New Tab"
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center p-3 bg-white/5 backdrop-blur-sm border-b border-white/10">
        <div className="flex space-x-2 mr-4">
          <button 
            onClick={handleGoBack}
            className={`p-2 rounded-lg transition-colors ${
              (currentIndex[activeTabId] || 0) <= 0
                ? 'bg-white/5 text-gray-500'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
            title="Back"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <button 
            onClick={handleGoForward}
            className={`p-2 rounded-lg transition-colors ${
              (currentIndex[activeTabId] || 0) >= ((history[activeTabId] || []).length - 1)
                ? 'bg-white/5 text-gray-500'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
            title="Forward"
          >
            <i className="fas fa-arrow-right"></i>
          </button>
          <button 
            onClick={handleRefresh}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            title="Refresh"
          >
            <i className="fas fa-redo"></i>
          </button>
        </div>
        
        <form onSubmit={handleAddressBarSubmit} className="flex-1">
          <div className="relative">
            <input 
              type="text" 
              value={addressBarUrl}
              onChange={(e) => setAddressBarUrl(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-white placeholder-gray-400"
              placeholder="Enter URL or search..."
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              </div>
            )}
          </div>
        </form>

        <div className="ml-4 flex items-center space-x-2">
          <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Home">
            <i className="fas fa-home"></i>
          </button>
          <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Bookmarks">
            <i className="fas fa-bookmark"></i>
          </button>
        </div>
      </div>
      
      {/* Browser Content */}
      <div className="flex-1 relative overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mb-4"></div>
              <p className="text-blue-300">Loading...</p>
            </div>
          </div>
        )}
        
        {activeTab?.isCustomPage ? (
          <div className="w-full h-full bg-gradient-to-br from-pink-900/30 to-purple-900/30 p-8 overflow-y-auto">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold mb-4 text-white">
                  ✨ Pink Pixel Links
                </h1>
                <p className="text-gray-300 text-lg">
                  Your gateway to Pink Pixel projects and iframe-friendly sites!
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold mb-4 text-pink-300">🎨 Pink Pixel Projects</h2>
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleNavigate('https://portfolio.pinkpixel.dev')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">Portfolio Website</div>
                      <div className="text-sm text-gray-400">portfolio.pinkpixel.dev</div>
                    </button>
                    <button 
                      onClick={() => window.open('https://github.com/pinkpixel-dev', '_blank')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">GitHub Profile</div>
                      <div className="text-sm text-gray-400">github.com/pinkpixel-dev</div>
                    </button>
                    <button 
                      onClick={() => window.open('https://www.buymeacoffee.com/pinkpixel', '_blank')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">Buy Me a Coffee</div>
                      <div className="text-sm text-gray-400">Support Pink Pixel</div>
                    </button>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold mb-4 text-blue-300">🌐 Iframe-Friendly Sites</h2>
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleNavigate('https://www.google.com')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">Google Search</div>
                      <div className="text-sm text-gray-400">www.google.com</div>
                    </button>
                    <button 
                      onClick={() => handleNavigate('https://wikipedia.org')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">Wikipedia</div>
                      <div className="text-sm text-gray-400">wikipedia.org</div>
                    </button>
                    <button 
                      onClick={() => handleNavigate('https://www.w3schools.com')}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="font-medium">W3Schools</div>
                      <div className="text-sm text-gray-400">www.w3schools.com</div>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-400/20 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <i className="fas fa-info-circle text-yellow-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-yellow-300 mb-2">About Iframe Restrictions</h3>
                    <p className="text-gray-300 text-sm">
                      Some websites (like GitHub, Facebook, etc.) block iframe embedding for security reasons. 
                      When you encounter these sites, they'll automatically open in a new browser tab instead. 
                      The sites listed above are known to work well within Pink Pixel Browser!
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8 text-gray-400 text-sm">
                <p>Made with ❤️ by Pink Pixel • Dream it, Pixel it ✨</p>
              </div>
            </div>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={activeTab?.url}
            className="w-full h-full border-0 bg-white"
            onLoad={handleIframeLoad}
            title={`Browser - ${activeTab?.title}`}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
          />
        )}
      </div>

      {/* Status Bar */}
      <div className="px-4 py-2 bg-gray-800/30 border-t border-white/10 text-xs text-gray-400 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span>🌐 {activeTab?.url}</span>
          {isLoading && <span className="text-blue-400">Loading...</span>}
        </div>
        <div className="flex items-center space-x-2">
          <span>✨ Pink Pixel Browser</span>
        </div>
      </div>
    </div>
  )
}

export default BrowserApp